import React, { Component } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { Alert, PageHeader, Tag, message , Button, Tabs } from 'antd';
import { CheckCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import qs from 'qs';


import { isTestModeOn } from '../../../utils/testMode';
import Spinner from '../../../components/Spinner';
import { BankAccountsList } from '../../../components/client/BankAccountComponents';
import { EditBankAccountForm, selectedCountryListDefault } from '../../../components/client/EditBankAccountForm';
import InlineWithdrawalsHistory from '../../../components/client/InlineWithdrawalsHistory';
import PayoutDetailsForm, { payoutDetailsMissing } from '../../../components/client/PayoutDetailsForm';
import StrikeClientModel from '../../../models/StrikeClientModel';
import BankAccountsModel from '../../../models/BankAccountsModel';
import StrikeAccountTransactionsModel from '../../../models/StrikeAccountTransactionsModel';
import {isBankUnique} from '../../../utils/PayoutUtils';
import FeatureFlagService from '../../../utils/FeatureFlagService';
import TrackerService from '../../../utils/TrackerService';
import MoneyJarService from '../../../utils/MoneyJarService';
import RedirectAndScrollTop from '../../../components/client/RedirectAndScrollTop';
import CenteredSpinner from '../../../components/CenteredSpinner';

function bankFilter(payout, isAdmin) {
  let isIncluded = false;
  const { hide, status, provider, accountNumber, routingNumber, holderName } = payout;
  const stripeStatuses = ['live', 'retry', 'waiting'];
  const moneyjarStatuses = ['live'];

  // Check if Stripe payout has bank details filled in
  const hasStripeBankDetails = accountNumber && routingNumber && holderName;

  switch (provider) {
    case 'stripeus':
      // Show 'ready' status only if bank details are filled in
      if (status === 'ready' && hasStripeBankDetails) {
        isIncluded = true;
      } else {
        isIncluded = (stripeStatuses.includes(status) || isAdmin || !hide);
      }
      break;
    case 'moneyjar':
      isIncluded = (moneyjarStatuses.includes(status) || isAdmin || !hide);
      break;
    default:
      isIncluded = (isAdmin || !hide);
      break;
  }
  return isIncluded;
}

const limitedCountries = ['US']; // MARK: USD limited USD countries

// /client/manage/profile
export default class BankClientProfileScreen extends Component {
  // Properties used by this component:

  constructor(props) {
    super(props);

    const clientId = this.props.match.params.clientId;
    const isAdmin = !!props.isAdmin;
    this.clientModel = new StrikeClientModel({ viewModel: this, clientId, isAdmin });
    this.model = new BankAccountsModel({ viewModel: this, clientId, filter: bankFilter });
    this.accountsModel = new StrikeAccountTransactionsModel({
      isTestMode: isTestModeOn(clientId),
      callback: this.updatedAccountsDataSource,
      viewModel: this,
      clientId,
      excludeTransactions: true,
      onUpdateBalances: this.updatedAccountsDataSource
    });

    this.state = {
      isEditing: false,
      bankSelected: null,
      clientId: clientId,
      client: null,
      accounts: [],
      loading: true,
      loadingPayouts: true,
      loadingAccounts: true,
      clientNotFound: false,
      mode: 'viewing',
      activeTab: 'accounts', // Track which tab is active
    };
  }

  getErrorMessageForErrorCode(error) {
    if (error) {
      if (error === '1') {
        return 'We haven’t been able to authenticate with your bank. Please check your details and try again';
      } else if (error === '2') {
        return 'Something has gone wrong. Please add your bank details manually below, or try again later'
      } else if (error === '3') {
        return 'You have already added this bank account';
      }
    }
    return 'Something has gone wrong. Please add your bank details manually below, or try again later'
  }

  componentDidMount() {
    const queryParams = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
    if (queryParams.errorCode){
      message.error(this.getErrorMessageForErrorCode(queryParams.errorCode), 7);
    } else if(queryParams.trueLayerSuccess){
      message.success('Bank account added successfully', 7);
    }
    
    // Set initial tab based on returnTo parameter
    if (queryParams.returnTo === 'history') {
      this.setState({ activeTab: 'history' });
    }
    
    this.clientModel.waitForClientData();
  }

  componentWillUnmount() {
    if(this.clientModel) this.clientModel.stop();
    if(this.model) this.model.stop();
    if(this.accountsModel) this.accountsModel.stop();
  }

  componentDidUpdate() {
  }

  isLoading = () => {
    return this.state.loading || this.state.loadingPayouts || this.state.loadingAccounts || false;
  }

  foundClient = async (client) => {
    this.setState(
      (state) => {
        return {
          client,
          loading: false,
          loadingPayouts: true,
          loadingAccounts: true,
        }
      }
    );

    this.model.fetchAllPayoutsData(this.clientModel.isAdmin);
    this.accountsModel.waitForAccountsData();
  }

  updatedPayoutsDataSource = (payouts) => {
    this.setState(
      (state) => {
        return {
          payouts,
          loadingPayouts: false,
        }
      }
    );
  }

  updatedAccountsDataSource = async (txns) => {
    const accounts = this.accountsModel.accounts;
    this.setState(
      (state) => {
        return {
          accounts,
          loadingAccounts: false,
        }
      }
    );
  }

  clientNotFound = () => {
    this.setState(
      (state) => {
        return {
          loading: false,
          clientNotFound: true,
        }
      }
    );
  }

  changeMode = (mode) => {
    if (mode === 'viewing') {
      return 'adding'
    } else if (mode === 'adding') {
      return 'saving'
    } else {
      return 'viewing';
    }
  }

  onClickAdd = () => {
    // A bank account can be added before any tip arrives, in every country.
    // The account is stored now; Stripe activates it on the first tip.
    this.setState((state) => {
      return {
        mode: this.changeMode(state.mode)
      }
    });
  };

  // Sign-up no longer collects date of birth / SSN last 4; the first bank account
  // is where Stripe needs them, so they are asked here, once, before the bank form.
  onSavePayoutDetails = async (values) => {
    const client = this.state.client;
    if (!client || !values) return;
    this.setState({ savingPayoutDetails: true });
    try {
      this.clientModel.client = client;
      await this.clientModel.commit(values);
      // The client subscription will echo this; apply it now so the bank form
      // opens without waiting on the snapshot.
      this.setState({ client: { ...client, ...values } });
    } catch (err) {
      message.error('Could not save your details. Please try again.');
    } finally {
      this.setState({ savingPayoutDetails: false });
    }
  };

  onClickEdit = (payoutId) => {
      const bank = this.model.payouts.filter((payout) => { return (payout.id === payoutId) })[0];
      this.setState((state) => {
        return {
          isEditing: true,
          bankSelected: bank,
          mode: this.changeMode(state.mode),
        }
      });
  };

  onClickDelete = (payoutId, hide) => {
    // Allow end users to hide (delete) their bank accounts, but only admins can restore
    const isAdmin = this.clientModel.isAdmin;
    const isHiding = !!hide; // hide=true means delete
    if (isHiding || isAdmin) {
      this.model.markPayoutHidden(payoutId, hide);
    }
  };

  showCashOutModal = (account) => {
    // This will be handled by the parent component's CashOut modal
    // For now, we'll just show a message that this should trigger the CashOut flow
    message.info(`Cash Out for ${account.currency} ${account.available.toFixed(2)} - This will open the Cash Out dialog`);
  };

  doneSaving = (payoutId, data) => {
    let redirectToPayoutId = null;
    if(payoutId && data) {
      const payout = { ...data, uid: payoutId }
      const moneyjarService = new MoneyJarService(payout);
      const isMoneyJarPayout = moneyjarService.isMoneyJarPayout();
      redirectToPayoutId = isMoneyJarPayout ? payoutId : null;
    }
    
    this.setState((state) => {
      return {
        bankSelected: null,
        isEditing: false,
        mode: this.changeMode(state.mode),
        redirectToPayoutId,
      }
    }
    );

    this.clientModel.waitForClientData();
  }

  internalSave = async (data) => {
    let payoutId = null;
    const isEditing = this.state.isEditing;
    if (data) {
      data.source = 'manual';
      data.isCompany = !!data.isCompany;
      data.descriptor = data.descriptor || '';
    }
    if (isEditing) {
      payoutId = this.state && this.state.bankSelected && this.state.bankSelected.id;
      await this.model.updateExistingPayout(payoutId, data);
    } else {
      payoutId = await this.model.makeNewPayoutObject(data);
    }
    this.doneSaving(payoutId, data);
    try {
      const tracker = new TrackerService({});
      const action = isEditing ? 'updated' : 'created';
      const addrCountry = data.address?.country || null;
      const bankCountry = data.country || null;
      tracker.mixpanelTrack('client_payout_address_changed', {
        client_id: this.state.clientId,
        action,
        country: addrCountry,
        country_name: addrCountry ? (selectedCountryListDefault[addrCountry] || null) : null,
        zip: data.address?.zip || null,
        town: data.address?.town || null,
      });
      tracker.mixpanelTrack('client_payout_bank_changed', {
        client_id: this.state.clientId,
        action,
        bank_name: data.displayName || null,
        country: bankCountry,
        country_name: bankCountry ? (selectedCountryListDefault[bankCountry] || null) : null,
        currency: data.currency || null,
        bic: data.bic || null,
        sort_code: data.sortCode || null,
        routing_number: data.routingNumber || null,
      });
    } catch (_trackErr) { /* analytics must never block saves */ }
  }

  onClickSave = async(data) => {
    const bankUnique = isBankUnique(data, this.state.payouts);
    const allowSave = this.state.isEditing || bankUnique;

    if (allowSave) {
      this.setState((state) => {
        return {
          mode: this.changeMode(state.mode),
        }
      });

      this.internalSave(data);
    } else {
      this.setState({duplicateBankAcc: true})
    }
  }

  resetDuplicateBankAccError = () => {
    this.setState({duplicateBankAcc: false})
  }

  onClickCancel = () => {
    this.setState((state) => {
      return {
        bankSelected: null,
        isEditing: false,
        mode: 'viewing',
      }
    }
    );
  }

  getButtons = () => {
    if (this.state.mode === 'viewing') {
      // A bank account can be added before any tip arrives, in every country.
      return [
        <Button key="1" type="text" shape='circle' icon={<PlusCircleOutlined style={{ fontSize: '28px', color :'#070'}}/>} onClick={this.onClickAdd} />
      ];
    } else if (this.state.mode === 'adding') {
      return [
        <Button key="1" type="danger" onClick={this.onClickCancel}>
          Cancel
        </Button>,
      ];
    } else {
      return [];
    }
  }

  render() {
    // Don't render anything until client is loaded
    if (!this.state.client) {
      return (
        <div style={{ height: '100%' }}>
          <PageHeader
            ghost={false}
            title={`Payouts`}
            onBack={(() => window.history.back())}
          />
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spinner condition={() => true} />
          </div>
        </div>
      );
    }

    const shouldNotUseTrueLayer = this.state.client && limitedCountries.includes(this.state.client.country);
    const trueLayerAttemptedAt = this.state.client && !!this.state.client.trueLayerAttemptedAt;
    const isManualEnabled = shouldNotUseTrueLayer || trueLayerAttemptedAt;
    const clientId = this.state.clientId;
    const isEditing = this.state.isEditing;
    const bankSelected = this.state.bankSelected;
    const redirectToPayoutId = this.state.redirectToPayoutId;

    if(redirectToPayoutId) {
      return (
        <RedirectAndScrollTop 
          to={`/${redirectToPayoutId}`}
          when={redirectToPayoutId}
        />
      );
    } else if (this.state.mode === 'viewing' && !this.state.loading) {
      return (
        <div style={{ height: '100%', textAlign: 'center'}}>
          <PageHeader
            ghost={false}
            title={`Payouts`}
            tags={this.state.mode === 'viewing' ? null : (this.state.mode === 'adding' ? <Tag color="blue">Adding</Tag> : <Tag color="green">Saving...</Tag>)}
            onBack={(() => window.history.back())}
            extra={this.getButtons()}
          />
          
          <Tabs 
            activeKey={this.state.activeTab} 
            onChange={(key) => this.setState({ activeTab: key })}
            style={{ padding: '0 12px' }}
          >
            <Tabs.TabPane tab="Bank accounts" key="accounts">
              <BankAccountsList
                isAdmin={this.clientModel.isAdmin}
                condition={() => { return !this.state.loadingPayouts }}
                data={this.state.payouts}
                client={this.state.client}
                accounts={this.state.accounts}
                onAddAccount={this.onClickAdd}
                onEditAccount={(payout) => { this.onClickEdit(payout) }}
                onDeleteAccount={(payout, hide) => { this.onClickDelete(payout, hide) }}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab="History" key="history">
              <InlineWithdrawalsHistory 
                clientId={this.state.clientId} 
                isAdmin={this.clientModel.isAdmin} 
                highlightWithdrawalId={qs.parse(this.props.location.search, { ignoreQueryPrefix: true }).highlight}
              />
            </Tabs.TabPane>
          </Tabs>
        </div>
      );
    } else if (!this.state.loading
      && !isEditing && !this.clientModel.isAdmin
      && payoutDetailsMissing(this.state.client, this.state.accounts).any) {
      // Gate: the payout account's own questions, before the first bank account.
      return (
        <div style={{ height: '100%' }}>
          <PageHeader
            ghost={false}
            title="Add Bank Account"
            extra={this.getButtons()}
          />
          <PayoutDetailsForm
            client={this.state.client}
            accounts={this.state.accounts}
            saving={!!this.state.savingPayoutDetails}
            onSave={this.onSavePayoutDetails}
          />
        </div>
      );
    } else if (!this.state.loading) {
      return (
        <div style={{ height: '100%' }}>
          { this.state.duplicateBankAcc &&
              <Alert
                message="Error"
                description="You have already added this bank account"
                type="error"
                showIcon
                closable
                afterClose={this.resetDuplicateBankAccError}
              />
          }
          <PageHeader
            ghost={false}
            title={isEditing ? `Edit Bank Account` : `Add Bank Account`}
            tags={this.state.mode === 'viewing' ? null : (this.state.mode === 'adding' ? null : <Tag color="green">Saving...</Tag>)}
            extra={this.getButtons()}
          />
          <EditBankAccountForm
            bank={bankSelected}
            edit={isEditing}
            callback={this.onClickSave}
            trueLayerAttemptedAt={isManualEnabled}
            isTrueLayerEnabled={!shouldNotUseTrueLayer}
            clientId={clientId}
            payouts={this.state.payouts}
            client={this.state.client}
          />
        </div>
      );
    } else {
      return (
        <div style={{ height: '100%' }}>
          <PageHeader
            ghost={false}
            title={`Payouts`}
            tags={this.state.mode === 'viewing' ? null : (this.state.mode === 'editing' ? <Tag color="blue">Add new</Tag> : <Tag color="green">Saving...</Tag>)}
            onBack={(() => window.history.back())}
            extra={this.getButtons()}
          />
        </div>
      );
    }


  }
}

